import { getPageviewDedupeKey } from './pageviewDedupeKey';

export type PageChangeContext = {
  previousKey: string | null;
  nextKey: string;
};

type PageChangeListener = (ctx: PageChangeContext) => void;
type ExitListener = () => void;

let installed = false;
let lastKnownKey: string | null = null;

const beforePageChangeListeners: PageChangeListener[] = [];
const afterPageChangeListeners: PageChangeListener[] = [];
const pageExitListeners: ExitListener[] = [];

let originalPushState: History['pushState'] | null = null;
let originalReplaceState: History['replaceState'] | null = null;
let popstateHandler: (() => void) | null = null;
let pagehideHandler: (() => void) | null = null;

function notifyNavigation() {
  if (typeof window === 'undefined') return;
  const nextKey = getPageviewDedupeKey(window.location);
  if (nextKey === lastKnownKey) return;
  const ctx: PageChangeContext = { previousKey: lastKnownKey, nextKey };
  for (const listener of beforePageChangeListeners) {
    listener(ctx);
  }
  lastKnownKey = nextKey;
  for (const listener of afterPageChangeListeners) {
    listener(ctx);
  }
}

function notifyPageExit() {
  for (const listener of pageExitListeners) {
    listener();
  }
}

export function installPageLifecycle(options: { trackRouteChanges?: boolean } = {}) {
  if (installed || typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }
  installed = true;
  const trackRouteChanges = options.trackRouteChanges ?? true;
  lastKnownKey = getPageviewDedupeKey(window.location);

  pagehideHandler = notifyPageExit;
  window.addEventListener('pagehide', pagehideHandler);

  if (trackRouteChanges) {
    originalPushState = history.pushState;
    originalReplaceState = history.replaceState;

    history.pushState = function (...args) {
      const result = originalPushState!.apply(this, args as [data: unknown, unused: string, url?: string | URL | null]);
      notifyNavigation();
      return result;
    };

    history.replaceState = function (...args) {
      const result = originalReplaceState!.apply(this, args as [data: unknown, unused: string, url?: string | URL | null]);
      notifyNavigation();
      return result;
    };

    popstateHandler = notifyNavigation;
    window.addEventListener('popstate', popstateHandler);
  }
}

export function onBeforePageChange(listener: PageChangeListener): () => void {
  beforePageChangeListeners.push(listener);
  return () => {
    const index = beforePageChangeListeners.indexOf(listener);
    if (index >= 0) beforePageChangeListeners.splice(index, 1);
  };
}

export function onAfterPageChange(listener: PageChangeListener): () => void {
  afterPageChangeListeners.push(listener);
  return () => {
    const index = afterPageChangeListeners.indexOf(listener);
    if (index >= 0) afterPageChangeListeners.splice(index, 1);
  };
}

export function onPageExit(listener: ExitListener): () => void {
  pageExitListeners.push(listener);
  return () => {
    const index = pageExitListeners.indexOf(listener);
    if (index >= 0) pageExitListeners.splice(index, 1);
  };
}

export function teardownPageLifecycle() {
  if (!installed || typeof window === 'undefined') return;
  if (originalPushState) history.pushState = originalPushState;
  if (originalReplaceState) history.replaceState = originalReplaceState;
  if (popstateHandler) window.removeEventListener('popstate', popstateHandler);
  if (pagehideHandler) window.removeEventListener('pagehide', pagehideHandler);
  originalPushState = null;
  originalReplaceState = null;
  popstateHandler = null;
  pagehideHandler = null;
  beforePageChangeListeners.length = 0;
  afterPageChangeListeners.length = 0;
  pageExitListeners.length = 0;
  lastKnownKey = null;
  installed = false;
}

export function isPageLifecycleInstalled(): boolean {
  return installed;
}
